import React from 'react';
import {
  Shield,
  FileText,
  Instagram,
  Facebook,
  Linkedin,
  Twitter
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Logo from '../assests/favicon-logo.png';
import Modal from './sub-components/UI/Modal';
import AboutModal from './sub-components/UI/AboutModal';
import ContactModal from './sub-components/UI/ContactModal';
import SolutionsModal from './sub-components/UI/SolutionsModal';
import ResourcesModal from './sub-components/UI/ResourcesModal';
import PrivacyPolicyModal from './sub-components/UI/PrivacyPolicyModal';
import { useModal } from '../hooks/useModal';
import { useAuth } from '../hooks/useAuth';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { openModal, closeModal, isOpen } = useModal();
  const { user } = useAuth();

  const footerLinks = {
    solutions: [
      { label: t('footer.links.solutions'), action: () => openModal('solutions') }
    ],
    resources: [
      { label: t('footer.links.resources'), action: () => openModal('resources') },
      // Only show FAQ when user is not logged in (welcome screen)
      ...(!user ? [{ label: t('footer.links.faq'), action: () => {
        const authenticationSection = document.querySelector('#faq');
        if (authenticationSection) {
          authenticationSection.scrollIntoView({ behavior: 'smooth' });
        }
      } }] : [])
    ],
    company: [
      { label: t('footer.modals.about.cta.title'), action: () => openModal('about') },
      { label: t('footer.modals.contact.form.title'), action: () => openModal('contact') }
    ]
  };

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/cvmatch.official/', label: t('footer.social.instagram') },
    { icon: Facebook, href: '#', label: t('footer.social.facebook') },
    { icon: Linkedin, href: '#', label: t('footer.social.linkedin') },
    { icon: Twitter, href: '#', label: t('footer.social.twitter') }
  ];

  return (
    <footer className="bg-slate-900 backdrop-blur-xl border-t border-indigo-500/20 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <div className="flex items-center">
                <img src={Logo} className='h-12 w-auto mr-3'/>
                {/* <h3 className="text-lg sm:text-xl font-bold text-white">
                  {t('app_name')}
                </h3> */}
              </div>
            </div>
            
            <p className="text-slate-500 leading-relaxed mb-6 font-extralight text-xs">
              {t('footer.brand.description')}
            </p>
            
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={index}
                    href={social.href}
                    className="w-10 h-10 bg-slate-800/50 rounded-xl flex items-center justify-center text-slate-400 hover:bg-gradient-to-br hover:from-indigo-500 hover:to-purple-600 hover:text-white transition-all duration-300 hover:scale-110"
                    aria-label={social.label}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links Sections */}
          <div>
            <h4 className="text-lg font-medium text-white mb-6">{t('footer.title_links.solutions')}</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.action || (() => {})}
                    className="text-slate-400 hover:text-indigo-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-medium text-white mb-6">{t('footer.title_links.resources')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.action}
                    className="text-slate-400 hover:text-indigo-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Information */}
          <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-white">
              {t('footer.legal_info')}
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li>
                <a
                  href="#privacy"
                  className="text-slate-400 hover:text-white transition-colors flex items-center"
                >
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{t('footer.privacy_policy')}</span>
                </a>
              </li>
              <li>
                <a
                  href="#terms"
                  className="text-slate-400 hover:text-white transition-colors flex items-center"
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{t('footer.terms_of_use')}</span>
                </a>
              </li>
              <li>
                <a
                  href="#cookies"
                  className="text-slate-400 hover:text-white transition-colors flex items-center"
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{t('footer.cookie_policy')}</span>
                </a>
              </li>
              <li>
                <a
                  href="#gdpr"
                  className="text-slate-400 hover:text-white transition-colors flex items-center"
                >
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">
                    {t('footer.gdpr_compliance')}
                  </span>
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-medium text-white mb-6">{t('footer.title_links.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.action || (() => {})}
                    className="text-slate-400 hover:text-indigo-400 transition-colors duration-200 text-sm text-left leading-relaxed"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact P-Tron Tech */}
          {/* <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-white">
              {t('footer.contact')}
            </h4>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex items-center">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-indigo-400 flex-shrink-0" />
                <a
                  href={`mailto:${t('footer.email')}`}
                  className="hover:text-white transition-colors truncate"
                >
                  {t('footer.email')}
                </a>
              </div>
              <div className="flex items-center">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-indigo-400 flex-shrink-0" />
                <span className="truncate">{t('footer.phone')}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-indigo-400 flex-shrink-0" />
                <span className="truncate">{t('footer.country')}</span>
              </div>
            </div>
          </div> */}

          {/* Useful links */}
          {/* <div className="space-y-3 sm:space-y-4">
            <h4 className="text-base sm:text-lg font-semibold text-white">
              {t('footer.resources')}
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li>
                <a
                  href="#help"
                  className="hover:text-white transition-colors block truncate"
                >
                  {t('footer.help_center')}
                </a>
              </li>
              <li>
                <a
                  href="#blog"
                  className="hover:text-white transition-colors block truncate"
                >
                  {t('footer.career_blog')}
                </a>
              </li>
              <li>
                <a
                  href="#api"
                  className="hover:text-white transition-colors block truncate"
                >
                  {t('footer.api_docs')}
                </a>
              </li>
              <li>
                <a
                  href="#status"
                  className="hover:text-white transition-colors flex items-center"
                >
                  <span className="truncate">{t('footer.service_status')}</span>
                  <ExternalLink className="w-2 h-2 sm:w-3 sm:h-3 ml-1 flex-shrink-0" />
                </a>
              </li>
            </ul>
          </div> */}
        </div>

        {/* Bottom Section */}
        <div className="border-t border-slate-700/50 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-xs sm:text-sm text-slate-400 text-center lg:text-left w-full lg:w-auto mb-4 md:mb-0">
              {/* <p className="mb-2">{t('footer.copyright')}</p>
              <p className="text-xs">{t('footer.collaboration')}</p> */}
            </div>
            
            {/* Compliance badges */}
            {/* <div className="flex flex-wrap items-center justify-center lg:justify-end gap-2 sm:gap-4 w-full lg:w-auto">
              <div className="flex items-center text-xs bg-green-900 text-green-300 px-2 sm:px-3 py-1 rounded-full">
                <Shield className="w-2 h-2 sm:w-3 sm:h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{t('footer.gdpr_compliant')}</span>
              </div>
              <div className="flex items-center text-xs bg-blue-900 text-blue-300 px-2 sm:px-3 py-1 rounded-full">
                <span className="truncate">{t('footer.swiss_made')}</span>
              </div>
              <div className="flex items-center text-xs bg-purple-900 text-purple-300 px-2 sm:px-3 py-1 rounded-full">
                <span className="truncate">{t('footer.ssl_secure')}</span>
              </div>
            </div> */}  
          </div>
        </div>

        {/* Detailed legal notices */}
        {/* <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-800 rounded-lg">
          <details className="text-xs text-gray-400">
            <summary className="cursor-pointer hover:text-white font-medium mb-2">
              {t('footer.legal_notices')}
            </summary>
            <section id="mentions-legales" className="scroll-mt-24">
                  <h3 className="text-lg font-semibold mb-4">Mentions légales</h3>
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2">Siège social</h4>
                      <p>P-TRON TECH<br />Baarerstrasse 112<br />CH-6300 Zoug<br />Courriel : support@cvmatchswiss.ch</p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Bureau de contact</h4>
                      <p>P-TRON TECH<br />Baarerstrasse 112<br />CH-6300 Zoug<br />Courriel : support@cvmatchswiss.ch</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        cvmatch.io est la plateforme d'emploi en ligne leader pour l'optimisation de 
                        candidatures en Suisse. Trouvez l'emploi qui vous plaît sur cvmatch.io.
                      </p>
                      <p className="mt-2">
                        cvmatch.io fait partie de P-TRON TECH, l'entreprise tech suisse leader sur le marché 
                        de l'emploi assisté par intelligence artificielle. P-TRON TECH propose une variété de 
                        solutions d'optimisation de candidatures innovantes. Son portefeuille comprend 
                        CVMatchSwiss, plateforme d'analyse intelligente de CV et de génération de lettres de 
                        motivation. Fondée en 2021 et établie à Zoug, l'entreprise est présente en Suisse et emploie 
                        actuellement une équipe dédiée. P-TRON TECH est synonyme d'efficacité, d'innovation et de 
                        transparence dans le secteur de l'optimisation de candidatures et aide les candidats à optimiser 
                        leurs CV ainsi qu'à générer des lettres de motivation personnalisées pour décrocher l'emploi 
                        de leurs rêves.
                      </p>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Informations légales</h4>
                      <ul className="space-y-1">
                        <li><strong>Forme juridique :</strong> Raison individuelle</li>
                        <li><strong>Secteur d'activité :</strong> Fourniture de services informatiques - Développement logiciel et applications</li>
                        <li><strong>Inscription au registre du commerce :</strong> 15.04.2021</li>
                        <li><strong>Numéro au registre du commerce :</strong> CH-170.1.010.871-6</li>
                        <li><strong>N° IDE/TVA :</strong> CHE-242.634.861</li>
                        <li><strong>Directeur de publication :</strong> P-TRON TECH</li>
                      </ul>
                    </div>
                  </div>
                </section>
          </details>
        </div> */}

        {/* Technical information */}
        <div className="mt-3 sm:mt-4">
          <p className="text-gray-400 text-sm">
            <span className="block sm:inline">
              {t('footer.version')}
            </span>
          </p>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={isOpen('about')}
        onClose={closeModal}
        title={t('footer.modals.about.title')}
      >
        <AboutModal />
      </Modal>

      <Modal
        isOpen={isOpen('contact')}
        onClose={closeModal}
        title={t('footer.modals.contact.title')}
      >
        <ContactModal />
      </Modal>

      <Modal
        isOpen={isOpen('solutions')}
        onClose={closeModal}
        title={t('footer.modals.solutions.title')}
      >
        <SolutionsModal />
      </Modal>

      <Modal
        isOpen={isOpen('resources')}
        onClose={closeModal}
        title={t('footer.modals.resources.title')}
      >
        <ResourcesModal />
      </Modal>

      <PrivacyPolicyModal
        isOpen={isOpen('privacy')}
        onClose={closeModal}
      />
    </footer>
  );
};

export default Footer;
